import { SeoController } from '@application/components';
import { useRouter, useShowToast, useValidation } from '@application/utils';
import type { ResetPasswordPageFormValues } from '@presentation/pages';
import { ResetPasswordPage } from '@presentation/pages';
import { confirmPasswordReset, getAuth, verifyPasswordResetCode } from 'firebase/auth';
import { useCallback, useMemo } from 'react';
import type { Schema } from 'yup';
import { object, ref, string } from 'yup';
import { useLogPasswordReset } from './api/useLogPasswordReset';

export const ResetPasswordPageController = () => {
  const { translate, infoFromCurrentPath, navigateToRoute } = useRouter();
  const { requiredMessage, passwordDifferentMessage, minCharactersMessage } = useValidation();
  const { showToast, showToastOnApolloError } = useShowToast();
  const { logPasswordReset } = useLogPasswordReset();

  const validationSchema = useMemo(() => {
    const schema: Schema<ResetPasswordPageFormValues> = object({
      password: string().min(6, minCharactersMessage(6)).required(requiredMessage),
      passwordConfirm: string()
        .oneOf([ref('password'), null], passwordDifferentMessage)
        .required(requiredMessage),
    }).defined();
    return schema;
  }, [minCharactersMessage, passwordDifferentMessage, requiredMessage]);

  const formSubmitHandler = useCallback(
    async (values: ResetPasswordPageFormValues) => {
      const auth = getAuth();
      const actionCode = infoFromCurrentPath.search.oobCode as string | undefined;
      logPasswordReset({ variables: { message: 'Attempt', actionCode } });
      if (!actionCode) {
        logPasswordReset({
          variables: { message: 'PWD:RESET No action code' },
        });
        showToast(
          'Action code is missing',
          { type: 'error' },
          { contactError: 'PWD:RESET Action code is missing' },
        );
        return;
      }

      try {
        await verifyPasswordResetCode(auth, actionCode);
      } catch (err) {
        logPasswordReset({
          variables: { message: `error: ${err?.message ?? JSON.stringify(err)}` },
        });
        showToast(
          translate(`common.toasts.firebaseError.${err?.code}`),
          { type: 'error' },
          { contactError: `resetPasswordSubmit : ${err?.code}` },
        );
        return;
      }
      try {
        await confirmPasswordReset(auth, actionCode, values.password);
        logPasswordReset({ variables: { actionCode, message: 'success' } });
        showToast(translate('common.toasts.changePasswordSuccess'), { type: 'success' });
        navigateToRoute('login');
      } catch (err) {
        logPasswordReset({
          variables: { actionCode, message: `PWD:RESET ${err?.message ?? JSON.stringify(err)}` },
        });
        showToastOnApolloError(err as any);
      }
    },
    [
      infoFromCurrentPath.search.oobCode,
      logPasswordReset,
      navigateToRoute,
      showToast,
      showToastOnApolloError,
      translate,
    ],
  );

  return (
    <>
      <SeoController noIndex />
      <ResetPasswordPage
        form={{
          validationSchema,
          onSubmit: formSubmitHandler,
          initialValues: {
            password: '',
            passwordConfirm: '',
          },
        }}
      />
    </>
  );
};
